import { box, button, column, link, styled, text } from '@tiendanube/nube-sdk-ui';
import { tokens } from './blocks.js';
import { thankYouTokens } from './thankYou.js';
import { actionLinkFillCss, buttonAnimationCss, buttonSubtitleCss, formButtonLabelCss, } from './theme.js';
import { validateConsent, validateFields } from './validate.js';
/**
 * The two merchant-authored ACTION buttons: the WhatsApp block and the generic link block.
 *
 * Their own module rather than more of `blocks.ts` for one concrete reason: the WhatsApp message
 * needs the **full shortcode set** (`thankYouTokens`), and `thankYou.ts` already imports
 * `blocks.ts` for the pricing helpers it interpolates from. Building these here breaks that cycle
 * instead of relying on ESM hoisting to paper over one.
 */
/** Shared look for the WhatsApp and link action buttons. */
function actionButtonStyle(style, styles) {
    return {
        // `.cod-action-btn` — a column so the subtitle sits under the label.
        ...styles.actionButton,
        backgroundColor: style.bg_color,
        color: style.text_color,
        borderRadius: `${style.border_radius}px`,
        border: style.border_width > 0 ? `${style.border_width}px solid ${style.border_color}` : 'none',
        fontSize: `${style.font_size}px`,
        // `applyButtonStyle` set this inline too (`form.ts:697`); the first port dropped it.
        ...(style.shadow > 0
            ? { boxShadow: `0 ${Math.round(style.shadow / 2)}px ${style.shadow}px rgba(0,0,0,.25)` }
            : {}),
    };
}
/**
 * A `link` carrying the merchant's chosen animation, or a plain one when they chose none.
 *
 * `styled()` is applied ONLY when there is an animation, so a config that wants none never depends
 * on that channel at all — the same layering the open button uses.
 */
function animatedLink(animation, props) {
    const css = buttonAnimationCss(animation);
    if (!css)
        return link(props);
    const Animated = styled(link) `
    ${css}
  `;
    return Animated(props);
}
/** The same, for the one action button that has to be a `button` — see `whatsappNode`. */
function animatedButton(animation, props) {
    const css = buttonAnimationCss(animation);
    if (!css)
        return button(props);
    const Animated = styled(button) `
    ${css}
  `;
    return Animated(props);
}
/**
 * `wa.me` deep link for a phone + an already-interpolated message.
 *
 * One definition, shared with the thank-you handoff in `form.ts`: the two build the same URL from
 * two merchant-authored phone numbers, and a difference in how the digits are stripped is a chat
 * that opens for one and not the other.
 */
export function whatsappHref(phone, message) {
    return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
}
/** Label (+ optional subtitle) as the inner column both action buttons carry. */
function actionContent(style, label, subtitle, styles) {
    return column({
        style: { ...styles.buttonContent, color: style.text_color },
        children: [
            // Through `styled()`, not the style prop — a theme's `p { margin-bottom }` beats an
            // inline `!important` and lifts the label off centre inside the button. Same fix and
            // reason as the submit button; see `formButtonLabelCss`.
            styled(text) `
        ${formButtonLabelCss(style.text_color)}
      `({
                style: styles.buttonLabel,
                children: [label],
            }),
            ...(subtitle
                ? [
                    styled(text) `
              ${buttonSubtitleCss(style.text_color)}
            `({
                        style: styles.submitSubtitle,
                        children: [subtitle],
                    }),
                ]
                : []),
        ],
    });
}
/**
 * WhatsApp button.
 *
 * ⚠️ A **`Link`, not a button.** `navigate()` is same-origin only, so a worker cannot open `wa.me`;
 * the buyer taps a link. Converged in the legacy bundle too (`MIGRATION.md` §12.0).
 *
 * 🚨 **`create_order` nests a link INSIDE a button, and that is load-bearing.** The merchant's
 * setting means "redirect as always, and record the order in Tiendanube while you do it" — one tap
 * that both navigates and reaches a handler. Neither component can do both alone:
 *
 *  - `link` takes no `onClick`. `NubeComponentLinkProps` declares none and the host's `nube-link`
 *    spends that slot on its own leave-the-store dialog — measured in `vanilla-adapter`.
 *  - `button` cannot navigate off-origin from a worker at all.
 *
 * What makes the pair work is the missing `stopPropagation` (`MIGRATION.md` §4.11): a nested
 * interactive component fires its parent's handler too. The host renders `<button onClick=…>` with
 * our children inside it, so the tap activates the anchor AND bubbles to the button. The order is
 * created on the way out.
 *
 * 🚨 **The order must not replace the form.** The host's leave-the-store dialog is a sibling of the
 * anchor INSIDE the link component, so anything that unmounts that subtree closes the dialog — and
 * the buyer never reaches WhatsApp. `FormSession` therefore reports this order as a success
 * *message* and leaves the tree standing; it deliberately does not set `submitted`.
 *
 * The button is also the validation gate. `create_order` is a submit, and a submit with an empty
 * name and phone has no order to create — so while the form is invalid this renders the button
 * WITHOUT the anchor: the tap validates, the errors appear, and nothing navigates. That is the
 * legacy behaviour, which closed the tab it had opened when `collect()` failed. Once the form is
 * valid the anchor is there and the same tap does both.
 *
 * Without a handler (the admin preview) it stays a plain link, so the Designer keeps showing the
 * button the merchant is styling rather than an inert control.
 */
export function whatsappNode(f, o) {
    const wa = f.whatsapp;
    if (!wa)
        return [];
    const onOrder = o.build.handlers?.onWhatsappOrder;
    if (!wa.create_order || !onOrder || o.build.mode === 'preview')
        return whatsappLink(f, o);
    const content = actionContent(wa.style, tokens(o, wa.text), wa.subtitle, o.styles);
    const ready = Object.keys(validateFields(o.config, o.build.state)).length === 0 &&
        !validateConsent(o.config, o.build.state);
    const FillLink = styled(link) `
    ${actionLinkFillCss(wa.style.text_color)}
  `;
    // 🚨 The BUTTON drops its padding and this inner box carries it, so that every tappable pixel
    // belongs to the anchor — a tap on the button's own padding would create the order and leave the
    // buyer on the product page.
    //
    // Which means the geometry has to be identical in both states, anchor or no anchor. It was not:
    // the invalid state rendered the bare label inside a zero-padding button, so the WhatsApp button
    // was a thin strip that grew to full size the moment the buyer ticked the terms box.
    const fill = {
        ...o.styles.actionButton,
        margin: '0',
        color: wa.style.text_color,
    };
    return [
        animatedButton(wa.style.animation, {
            style: { ...actionButtonStyle(wa.style, o.styles), padding: '0' },
            // 🚨 Never `disabled`. A disabled `<button>` swallows clicks from its own descendants, which
            // would take the anchor down with it. `FormSession.submit` is what refuses a second order.
            onClick: () => onOrder(f.key),
            children: [
                ready
                    ? FillLink({
                        href: whatsappHref(wa.phone, thankYouTokens(o, wa.message)),
                        target: '_blank',
                        style: fill,
                        children: [content],
                    })
                    : box({ style: fill, children: [content] }),
            ],
        }),
    ];
}
/**
 * The plain WhatsApp deep link — the block as it renders whenever it is not also creating an order.
 *
 * The message goes through `thankYouTokens`, not `tokens()`: everything the admin advertises for
 * this field resolves against `FormState`, so the buyer's own answers reach the chat. Only
 * `{order_number}` / `{order_id}` need an order, and they are empty until one exists.
 */
export function whatsappLink(f, o) {
    const wa = f.whatsapp;
    if (!wa)
        return [];
    return [
        animatedLink(wa.style.animation, {
            href: whatsappHref(wa.phone, thankYouTokens(o, wa.message)),
            target: '_blank',
            style: actionButtonStyle(wa.style, o.styles),
            children: [actionContent(wa.style, tokens(o, wa.text), wa.subtitle, o.styles)],
        }),
    ];
}
/** Generic link button — same treatment, merchant-supplied URL. */
export function linkButtonNode(f, o) {
    const lk = f.link;
    if (!lk?.url)
        return [];
    return [
        animatedLink(lk.style.animation, {
            href: lk.url,
            target: '_blank',
            style: actionButtonStyle(lk.style, o.styles),
            children: [actionContent(lk.style, lk.text, lk.subtitle, o.styles)],
        }),
    ];
}
//# sourceMappingURL=actions.js.map